import React, { useMemo, useState } from "react";
import { Sparkles, RotateCw } from "lucide-react";

const inputCls = "w-full border border-slate-200 bg-white rounded-lg px-3 py-2.5 outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-colors";
const labelCls = "text-xs font-semibold uppercase tracking-widest text-slate-500 block mb-2";
const btnCls = "inline-flex items-center gap-2 bg-indigo-600 text-white rounded-lg px-5 py-2.5 font-medium hover:bg-indigo-700 active:scale-95 transition disabled:opacity-50";

// Safe arithmetic evaluation: the backend validator already whitelists the formula
// charset to digits, input keys and + - * / ( ) %, and rejects unknown identifiers.
function evaluate(formula, values) {
  if (!/^[\w\s+\-*/().%]+$/.test(formula)) throw new Error("bad formula");
  const keys = Object.keys(values);
  const expr = formula.replace(/\b\d+(\.\d+)?%/g, (m) => `(${parseFloat(m) / 100})`);
  for (const tok of expr.match(/[a-zA-Z_][a-zA-Z0-9_]*/g) || []) {
    if (!keys.includes(tok)) throw new Error("unknown key");
  }
  const fn = new Function(...keys, `"use strict"; return (${expr});`);
  const out = fn(...keys.map((k) => Number(values[k]) || 0));
  if (typeof out !== "number" || !isFinite(out)) throw new Error("bad result");
  return out;
}

function formatResult(value, format) {
  if (format === "currency") return "$" + value.toLocaleString(undefined, { maximumFractionDigits: value < 100 ? 2 : 0 });
  if (format === "percent") return (Math.round(value * 10) / 10) + "%";
  return Math.round(value * 100) / 100;
}

export default function GenericToolRunner({ spec }) {
  const initial = useMemo(() => {
    const v = {};
    for (const inp of spec.inputs || []) {
      if (inp.type === "yesno") v[inp.key] = null;
      else if (inp.type === "select") v[inp.key] = (inp.options && inp.options[0] && inp.options[0].value) ?? inp.default ?? 0;
      else v[inp.key] = inp.default ?? "";
    }
    return v;
  }, [spec]);
  const [values, setValues] = useState(initial);
  const [result, setResult] = useState(null);

  const set = (k, val) => { setValues((s) => ({ ...s, [k]: val })); setResult(null); };

  const run = () => {
    try {
      let score;
      if (spec.kind === "scorecard") {
        score = (spec.inputs || []).reduce((sum, inp) => sum + (values[inp.key] === true ? (Number(inp.weight) || 1) : 0), 0);
      } else {
        score = evaluate(spec.formula, values);
      }
      const bands = [...(spec.bands || [])].sort((a, b) => (a.min || 0) - (b.min || 0));
      const band = bands.filter((b) => score >= (b.min || 0)).pop() || bands[0] || null;
      setResult({ score, band });
    } catch {
      setResult({ error: true });
    }
  };

  const ready = (spec.inputs || []).every((inp) =>
    inp.type === "yesno" ? values[inp.key] !== null : values[inp.key] !== "" && values[inp.key] !== undefined);

  return (
    <div data-testid={`generated-tool-${spec.slug}`}>
      <div className="grid sm:grid-cols-2 gap-4">
        {(spec.inputs || []).map((inp) => (
          <div key={inp.key} className={inp.type === "yesno" ? "sm:col-span-2" : ""}>
            <label className={labelCls}>{inp.label}</label>
            {inp.type === "select" && (
              <select className={inputCls} value={values[inp.key]} onChange={(e) => set(inp.key, Number(e.target.value))} data-testid={`input-${inp.key}`}>
                {(inp.options || []).map((o, i) => (
                  <option key={i} value={typeof o === "object" ? o.value : i + 1}>{typeof o === "object" ? o.label : o}</option>
                ))}
              </select>
            )}
            {inp.type === "number" && (
              <input type="number" className={inputCls} value={values[inp.key]} min={inp.min} max={inp.max} step={inp.step || "any"}
                onChange={(e) => set(inp.key, e.target.value)} data-testid={`input-${inp.key}`} />
            )}
            {inp.type === "yesno" && (
              <div className="flex gap-2">
                {[["Yes", true], ["No", false]].map(([lbl, val]) => (
                  <button key={lbl} type="button" onClick={() => set(inp.key, val)} data-testid={`input-${inp.key}-${lbl.toLowerCase()}`}
                    className={`px-4 py-2 rounded-lg border text-sm font-medium transition ${values[inp.key] === val ? "bg-indigo-600 text-white border-indigo-600" : "border-slate-200 hover:bg-slate-50"}`}>
                    {lbl}
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="mt-6 flex items-center gap-3">
        <button onClick={run} disabled={!ready} className={btnCls} data-testid="tool-run-button">
          <Sparkles className="w-4 h-4" /> Calculate
        </button>
        <button onClick={() => { setValues(initial); setResult(null); }} className="inline-flex items-center gap-2 border border-slate-200 rounded-lg px-4 py-2.5 text-sm hover:bg-slate-50 transition" data-testid="tool-reset-button">
          <RotateCw className="w-4 h-4" /> Reset
        </button>
      </div>
      {result && !result.error && (
        <div className="mt-8 rounded-2xl border border-indigo-100 bg-indigo-50/50 p-6" data-testid="tool-result">
          <p className="text-[10px] font-mono uppercase tracking-widest text-indigo-600">{spec.result_label || "Your result"}</p>
          <p className="font-display text-4xl mt-1 text-slate-900">{formatResult(result.score, spec.result_format)}</p>
          {result.band && (
            <div className="mt-4">
              <p className="font-semibold text-slate-900">{result.band.label}</p>
              <p className="text-slate-600 text-sm mt-1 leading-relaxed">{result.band.advice}</p>
            </div>
          )}
        </div>
      )}
      {result && result.error && (
        <p className="mt-6 text-sm text-red-600" data-testid="tool-error">That combination could not be computed — check your inputs.</p>
      )}
    </div>
  );
}
